import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Users, FileText, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const TermsOfService = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: "acceptance",
      title: "1. Aceitação dos Termos",
      icon: <Shield className="h-6 w-6 text-[#00B6F0]" />,
      content: `Ao acessar e utilizar a plataforma Arena Pro Player, você concorda em cumprir e estar vinculado aos presentes Termos de Uso. Se você não concordar com qualquer parte destes termos, não deverá utilizar nossa plataforma.

Estes termos se aplicam a todos os usuários da plataforma, incluindo visitantes, jogadores, mentores e administradores.`
    },
    {
      id: "services",
      title: "2. Descrição dos Serviços",
      icon: <Users className="h-6 w-6 text-[#00B6F0]" />,
      content: `A Arena Pro Player é uma plataforma que conecta jogadores com mentores profissionais para coaching em videogames. Nossos serviços incluem:

• Conexão entre jogadores e mentores especializados
• Agendamento e gestão de sessões de mentoria
• Sistema de pagamentos integrado
• Ferramentas de comunicação e aprendizado
• Acompanhamento de progresso e resultados

A plataforma facilita essas conexões, mas não é responsável pela qualidade específica do conteúdo das mentorias.`
    },
    {
      id: "registration",
      title: "3. Registro e Conta de Usuário",
      icon: <FileText className="h-6 w-6 text-[#00B6F0]" />,
      content: `Para utilizar nossos serviços, você deve:

• Criar uma conta fornecendo informações precisas e atualizadas
• Ser maior de 18 anos ou ter autorização dos responsáveis legais
• Manter a confidencialidade de suas credenciais de acesso
• Notificar-nos imediatamente sobre qualquer uso não autorizado da sua conta
• Aceitar a responsabilidade por todas as atividades realizadas em sua conta

Reservamos o direito de suspender ou encerrar contas que violem estes termos.`
    },
    {
      id: "conduct",
      title: "4. Conduta do Usuário",
      icon: <AlertTriangle className="h-6 w-6 text-[#00B6F0]" />,
      content: `Os usuários se comprometem a:

• Tratar todos os membros da comunidade com respeito
• Não utilizar a plataforma para atividades ilegais ou prejudiciais
• Não compartilhar conteúdo ofensivo, discriminatório ou inadequado
• Respeitar os direitos de propriedade intelectual
• Não tentar comprometer a segurança da plataforma
• Fornecer feedback construtivo e honesto

Comportamentos inadequados podem resultar em suspensão ou banimento da plataforma.`
    },
    {
      id: "payments",
      title: "5. Pagamentos e Reembolsos",
      icon: <FileText className="h-6 w-6 text-[#00B6F0]" />,
      content: `• Todos os pagamentos são processados através de parceiros de pagamento seguros
• Os preços estão sujeitos a alterações, mas mudanças não afetam compras já realizadas
• Reembolsos são processados conforme nossa Política de Reembolsos
• A Arena Pro Player cobra uma taxa de serviço sobre as transações
• Mentores são responsáveis por declarer sua renda conforme a legislação vigente`
    },
    {
      id: "liability",
      title: "6. Limitação de Responsabilidade",
      icon: <Shield className="h-6 w-6 text-[#00B6F0]" />,
      content: `A Arena Pro Player atua como intermediária entre jogadores e mentores. Nossa responsabilidade é limitada a:

• Fornecer a plataforma e ferramentas necessárias
• Facilitar pagamentos de forma segura
• Moderar conteúdo reportado pelos usuários

Não nos responsabilizamos por:
• Qualidade específica das mentorias
• Resultados individuais dos usuários
• Disputas diretas entre usuários
• Problemas técnicos de terceiros`
    },
    {
      id: "changes",
      title: "7. Modificações dos Termos",
      icon: <FileText className="h-6 w-6 text-[#00B6F0]" />,
      content: `Reservamos o direito de modificar estes Termos de Uso a qualquer momento. Mudanças significativas serão comunicadas através de:

• Notificações na plataforma
• E-mail para usuários registrados
• Aviso em nossa página principal

O uso continuado da plataforma após mudanças constitui aceitação dos novos termos.`
    },
    {
      id: "contact",
      title: "8. Contato",
      icon: <Users className="h-6 w-6 text-[#00B6F0]" />,
      content: `Para dúvidas sobre estes Termos de Uso, entre em contato:

• WhatsApp: (11) 99642-5204
• E-mail: suporte@arenaproplayer.com
• Horário de atendimento: Segunda a Sexta, 9h às 18h

Nossa equipe está pronta para esclarecer qualquer questão sobre o uso da plataforma.`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1117] via-[#0f141c] to-[#0c1117]">
      {/* Header */}
      <div className="bg-black/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Termos de <span className="text-[#00B6F0]">Uso</span>
            </h1>
            <p className="text-gray-300 text-lg">
              Última atualização: Janeiro de 2024
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-8"
        >
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-[#00B6F0]/30 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                {section.icon}
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {section.title}
                </h2>
              </div>
              
              <div className="text-gray-300 leading-relaxed space-y-4">
                {section.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 p-6 bg-[#00B6F0]/10 rounded-xl border border-[#00B6F0]/30"
        >
          <h3 className="text-xl font-bold text-white mb-3">
            📋 Resumo Importante
          </h3>
          <p className="text-gray-300 leading-relaxed">
            Estes Termos de Uso estabelecem as regras para uso da Arena Pro Player. 
            Ao usar nossa plataforma, você concorda com estes termos e se compromete 
            a seguir nossas diretrizes de comunidade. Para dúvidas, nossa equipe 
            está sempre disponível para ajudar.
          </p>
        </motion.div>
      </div>
    </div>
  );
}; 